/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import AreaDataset from '@fusioncharts/charts/src/dataset/area';
import radarAnimation from './index.animation';
import { addDep } from '@fusioncharts/core/src/dependency-manager';
import { getCoordinates, getPlotFromPixel } from '@fusioncharts/utils/src/scale-utils/polar-util';
import { BLANKSTRING, pluck, toRaphaelColor, getFirstValue, regex,
  getLightColor, HASHSTRING, extend2, pluckNumber } from '@fusioncharts/core/src/lib';

var COMMASTRING = ',',
  UNDEF,
  M = 'M',
  L = 'L',
  Z = 'Z',
  dropHash = regex.dropHash;
addDep({
  name: 'radarAnimation',
  type: 'animationRule',
  extension: radarAnimation
});
/**
 * Creates the Radar Dataset
 * @class
 */
class RadarDataset extends AreaDataset {
  constructor () {
    super();
    // eslint-disable-next-line good-practices/no-function-dependency
    this.getPathArr = function () {
      var pathObj = this,
        pathArr = pathObj.pathArr,
        path2Arr = pathObj.path2Arr;

      if (pathArr.length || path2Arr.length) {
        return pathArr.concat(path2Arr);
      }
      return [];
    };
  }
  /**
   * return name
   * @return {string} - component name
   */
  getName () {
    return 'radar';
  }
  /**
   * Create pixel coordinates for the plots
   */
  createCoordinates () {
    var dataSet = this,
      chartConfig = dataSet.getFromEnv('chartConfig'),
      dataObj,
      config,
      previousY,
      i,
      Px,
      Py,
      len = dataSet.components.data.length, // eslint-disable-line good-practices/no-single-usage-variable
      dataStore = dataSet.components.data, // eslint-disable-line good-practices/no-single-usage-variable
      xPos,
      yPos,
      setValue;
    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      config = dataObj && dataObj.config;
      setValue = config.setValue;

      if (dataObj === UNDEF) {
        continue;
      }

      yPos = dataSet.getFromEnv('yAxis').getPixel(setValue + (previousY || 0));
      xPos = getCoordinates({
        radius: (chartConfig.canvasTop + chartConfig.canvasHeight / 2) - yPos,
        theta: i
      }, dataSet.getFromEnv('xAxis'));
      // xPos = xAxis.getPixel(i, (chartConfig.canvasTop + chartConfig.canvasHeight / 2) - yPos);
      Py = xPos.y;
      Px = xPos.x;

      config._Px = Px;
      config._Py = Py;
      config._Pbx = Px;
      config._Pby = Py;
    }
  }
  /**
   * Gets the path for the line plot.
   * @param {Object} data data object
   * @param {Object} positions position object
   * @param {boolean} type represents whether it is of type 'base'
   * @return {Object} path details
   */
  getLinePath (data, positions, type) {
    var dataset = this,
      chartConfig = dataset.getFromEnv('chartConfig'), // eslint-disable-line good-practices/no-single-usage-variable
      obj = {},
      // Used for determining whether the last value was valid
      // Initially it is false
      lastValidValue = obj.lastValidValue || false,
      temp = obj.temp || [],
      temp2 = obj.temp2 || [], // eslint-disable-line good-practices/no-single-usage-variable
      pathArr = obj.pathArr || [],
      i,
      config,
      xPos,
      yPos,
      pointsJoined = obj.pointsJoined || 0,
      dataObj,
      setValue,
      startPos = (positions && positions.begin) || 0, // eslint-disable-line good-practices/no-single-usage-variable
      endPos = (positions && positions.end) || data.length, // eslint-disable-line good-practices/no-single-usage-variable
      step = chartConfig.viewPortConfig.step || 1, // eslint-disable-line good-practices/no-single-usage-variable
      yAxis = dataset.getFromEnv('yAxis'),
      xAxis = dataset.getFromEnv('xAxis'),
      baseZero = yAxis.getPixel(0), // eslint-disable-line good-practices/no-single-usage-variable
      removeDataLen = dataset.removeDataLen || 0, // eslint-disable-line good-practices/no-single-usage-variable
      dataWithRemovedPaths = [];
    // Added current dataStore to arr
    dataWithRemovedPaths = dataWithRemovedPaths.concat(data);

    for (i = startPos; i < endPos + removeDataLen; i += step) {
      dataObj = dataWithRemovedPaths[i];
      if (!dataObj) {
        continue;
      }

      config = dataObj.config;
      setValue = config.setValue;
      if (setValue === null || (config && config.isSkipped === true)) {
        // xPos = xAxis.getPixel(xAxis.getLimit().min).x;
        xPos = getCoordinates({
          theta: xAxis.getLimit().min
        }, xAxis).x;
        yPos = yAxis.getPixel(yAxis.config.axisRange.min);
      } else {
        xPos = config._Px;
        yPos = config._Py;
      }

      // take base or zero y value for animation
      if (type === 'zero') {
        yPos = baseZero;
      } else if (type === 'base') {
        yPos = config._Pby;
      }
      if (lastValidValue) {
        if (temp.length) {
          pathArr = pathArr.concat(temp);
          temp = [];
          pointsJoined++;
        }
        // Push the upper x and y position to pathArr
        pathArr.push([L, xPos, yPos]);
      } else {
        // Push the upper x and y positions of area plot to temp
        temp.push([M, xPos, yPos]);
        // No points are joined when starting a new path
        pointsJoined = 0;
        lastValidValue = true;
      }
    }
    // If path is not closed and any points are joined then close the path
    if (pathArr[pathArr.length - 1] !== Z && pointsJoined > 0) {
      pathArr.push(Z);
    }
    return {
      pathArr: pathArr,
      path2Arr: [],
      lastValidValue: lastValidValue,
      pointsJoined: pointsJoined,
      temp: temp,
      temp2: temp2,
      getPathArr: dataset.getPathArr
    };
  }
  /**
   * Parses all the attributes for dataset level and set level
   * Called from init function of area class and line class
   * Both line and area attributes configuration is done here
   * @param {Object} datasetJSON the data provided by user
   * @return {boolean} false if datasetJSON is undefined
   */
  configureAttributes (datasetJSON) {
    if (!datasetJSON) {
      return false;
    }
    this.trimData(datasetJSON);
    this.JSONData = datasetJSON;
    var dataset = this,
      conf = dataset.config,
      chart = dataset.getFromEnv('chart'), // eslint-disable-line good-practices/no-single-usage-variable
      chartAttr = chart.getFromEnv('dataSource').chart,
      JSONData = dataset.JSONData,
      plotColor = dataset.getFromEnv('color-manager').getPlotColor(dataset.index);
    super.configureAttributes(datasetJSON);
    conf.defaultPadding = {
      left: 0,
      right: 0
    };
    conf.plotfillcolor = pluck(JSONData.color,
      chartAttr.plotfillcolor, plotColor);
    conf.plotbordercolor = pluck(JSONData.plotbordercolor, chartAttr.plotbordercolor,
      chartAttr.areabordercolor, plotColor).split(COMMASTRING)[0];

    conf.plotborderColorObject = {
      color: conf.plotbordercolor,
      alpha: conf.plotborderalpha,
      angle: conf.plotfillangle
    };
    conf.fillColor = {
      color: conf.plotfillcolor + (conf.plotgradientcolor
        ? COMMASTRING + conf.plotgradientcolor : BLANKSTRING),
      alpha: conf.plotfillalpha,
      angle: conf.plotfillangle
    };
    conf.legendSymbolColor = conf.plotfillcolor;
  }
  /**
   * Function that return the nearest plot details
   * @param   {number} chartX x-axis position of the mouse cordinate
   * @param   {number} chartY y-axis position of the mouse cordinate
   * @return {Object} return an object with details of nearest plot and whether it is
   *                   hovered or not
   */
  _getHoveredPlot (chartX, chartY) {
    var dataset = this,
      xAxis = dataset.getFromEnv('xAxis'),
      dataStore = dataset.components.data,
      pointObj,
      xMin,
      xMax,
      len = dataStore.length,
      returnValue,
      conf = dataset.config,
      divAngle = 360 / len,
      i;

    xMin = Math.floor(Math.max(getPlotFromPixel({ // eslint-disable-line good-practices/no-single-usage-variable
      x: chartX - conf.maxRadius,
      y: chartY
    }, xAxis) / divAngle - 1, 0));

    xMax = Math.floor(Math.max(getPlotFromPixel({ // eslint-disable-line good-practices/no-single-usage-variable
      x: chartX + conf.maxRadius,
      y: chartY
    }, xAxis) / divAngle, len - 1));

    for (i = xMax; i >= xMin; i--) {
      pointObj = dataStore[i];
      if (pointObj) {
        returnValue = dataset.isWithinShape(pointObj, i, chartX, chartY);
        if (returnValue) {
          break;
        }
      }
    }

    return returnValue;
  }

  /**
   * function to check if the context of dataset is chanegd or not
   * if changed then it will return staus true. It is for further infomormation to redraw a dataset
   *
   * @returns {boolean} status of context change
   * @memberof RadarDataset
   */
  _contextChanged () {
    if (!this.config.context) {
      this.config.context = {};
    }
    var dataset = this,
      status,
      context = dataset.config.context,
      oldAxisCenterX = context.axisCenterX, // eslint-disable-line good-practices/no-single-usage-variable
      newAxisCenterX;

    // Find new center of polar category axis.
    newAxisCenterX = dataset.getFromEnv('xAxis').config.axisDimention.centerX;
    // If new center of polar category axis is different from old center, redraw is needed.
    status = !(newAxisCenterX === oldAxisCenterX); // eslint-disable-line good-practices/no-single-usage-variable
    context.axisCenterX = newAxisCenterX;

    return status || super._contextChanged();
  }
  /**
   * Returns the details of the plot in the category band that the givennt x and y co-ordinates
   * are in.
   *
   * @param {number} [chartX=0] x-axis position of the mouse cordinate
   * @param {number} [chartY=0] y-axis position of the mouse cordinate
   *
   * @return {Object} Return an object with details of nearest plot in the category band.
   */
  getPlotInCategoryAt (chartX, chartY) {
    let plots = this.components.data, // eslint-disable-line good-practices/no-single-usage-variable
      categoryAxis = this.getFromEnv('xAxis'),
      isDatasetVisible = this.getState('visible'),
      hoveredCategoryIndex = Math.round(getPlotFromPixel({
        x: chartX,
        y: chartY
      }, categoryAxis)),
      foundPlot = plots.find((plot, idx, plotArr) => {
        let plotCategoryIndex,
          nextPlotCategoryIndex,
          prevPlotCategoryIndex,
          halfCategoryDiff,
          nextPlot,
          prevPlot;

        if (idx === 0) {
          nextPlot = plotArr[idx + 1];
          // For the first plot, the previous plot will be the last plot in the array of plots
          prevPlot = plotArr[plotArr.length - 1];
        } else if (idx === plotArr.length - 1) {
          // For the last plot, the next plot will be the first plot in the array of plots
          nextPlot = plotArr[0];
          prevPlot = plotArr[plotArr.length - 2];
        } else {
          // For any other plot just select the plot immediately before and after the current plot
          nextPlot = plotArr[idx + 1];
          prevPlot = plotArr[idx - 1];
        }
        // plotCategoryIndex = categoryAxis.getAngle(plot.config._Px, plot.config._Py);
        plotCategoryIndex = getPlotFromPixel({ // eslint-disable-line good-practices/no-single-usage-variable
          x: plot.config._Px,
          y: plot.config._Py
        }, categoryAxis);

        // For the last plot, the category band will end at 360 degrees
        nextPlotCategoryIndex = idx === plotArr.length - 1
          ? 360
          // categoryAxis.getAngle(nextPlot.config._Px, nextPlot.config._Py);
          : getPlotFromPixel({
            x: nextPlot.config._Px,
            y: nextPlot.config._Py
          }, categoryAxis);
        // prevPlotCategoryIndex = categoryAxis.getAngle(prevPlot.config._Px, prevPlot.config._Py);
        prevPlotCategoryIndex = getPlotFromPixel({ // eslint-disable-line good-practices/no-single-usage-variable
          x: prevPlot.config._Px,
          y: prevPlot.config._Py
        }, categoryAxis);
        // The angle at which the category ends and the next category starts
        halfCategoryDiff = (nextPlotCategoryIndex - plotCategoryIndex) / 2;

        if (hoveredCategoryIndex <= 0 + halfCategoryDiff) {
          // If the mouse is over an angle less than where the first category ends
          return (hoveredCategoryIndex >= 0 && hoveredCategoryIndex <= 0 + halfCategoryDiff);
        } else if (hoveredCategoryIndex > 360 - halfCategoryDiff) {
          // If the mouse is over an angle greater than where the last category band ends
          return hoveredCategoryIndex >= 360 - halfCategoryDiff && hoveredCategoryIndex <= 360;
        }
        return (
          hoveredCategoryIndex >= prevPlotCategoryIndex + halfCategoryDiff &&
            hoveredCategoryIndex <= nextPlotCategoryIndex - halfCategoryDiff
        );
      }),
      hoveredPlotInfo = this._getHoveredPlot(chartX, chartY);

    if (isDatasetVisible && hoveredPlotInfo) {
      // If dataset is visible and the mouse is exactly over a plot
      return hoveredPlotInfo;
    } else if (isDatasetVisible && foundPlot) {
      // If dataset is visible and the mouse is over a category band
      return {
        pointIndex: foundPlot._index,
        hovered: false,
        pointObj: foundPlot
      };
    }
    // If dataset is not visible and no relevant plot was found
    return false;
  }

  /**
   * Function to register datasets in the internal data store of legend class.
   */
  _addLegend () {
    var dataset = this,
      strokeColor,
      fillColor,
      conf = dataset.config,
      chartAttr = dataset.getFromEnv('chart-attrib'),
      legendItem,
      legend = dataset.getFromEnv('legend'),
      color = conf.legendSymbolColor,
      lightColor,
      use3DLighting = pluckNumber(chartAttr.use3dlighting, chartAttr.useplotgradientcolor, 1); // eslint-disable-line good-practices/no-single-usage-variable
    strokeColor = getLightColor(color, 60).replace(dropHash, HASHSTRING); // eslint-disable-line good-practices/no-single-usage-variable
    if (use3DLighting) {
      lightColor = getLightColor(color, 40);
      fillColor = {
        FCcolor: {
          color: color + ',' + color + ',' + lightColor + ',' + color + ',' + color,
          ratio: '0,70,30',
          angle: 270,
          alpha: '100,100,100,100,100'
        }
      };
    } else {
      fillColor = {
        FCcolor: {
          color: color,
          angle: 0,
          ratio: '0',
          alpha: '100'
        }
      };
    }
    if (conf.includeinlegend) {
      legendItem = legend.getItem(dataset.config.legendItemId);
      if (!legendItem) {
        dataset.config.legendItemId = legend.createItem(dataset);
        legendItem = legend.getItem(dataset.config.legendItemId);
        dataset.addExtEventListener('fc-click', function () {
          legendItem.itemClickFn();
        }, legendItem);
      }
      legendItem.configure({
        enabled: conf.includeInLegend,
        type: dataset.type,
        label: getFirstValue(dataset.JSONData.seriesname),
        legendIconAlpha: pluckNumber(dataset.JSONData.legendiconalpha),
        usePattern: conf.usePattern,
        patternAttr: {
          patternType: conf.patternType,
          patternAngle: conf.patternAngle,
          patternDensity: conf.patternDensity,
          patternSize: conf.patternSize,
          color: conf.plotColor,
          patternAlpha: conf.patternAlpha,
          patternBgColor: conf.patternBgColor,
          alpha: conf.plotfillalpha
        }
      });
      legendItem.setStateCosmetics('default', {
        symbol: {
          fill: toRaphaelColor(fillColor),
          bgAlpha: pluckNumber(dataset.JSONData.legendiconbgalpha, dataset.JSONData.legendiconalpha, chartAttr.legendiconbgalpha, chartAttr.legendiconalpha, conf.plotfillalpha),
          borderAlpha: pluckNumber(dataset.JSONData.legendiconborderalpha, dataset.JSONData.legendiconalpha, chartAttr.legendiconborderalpha, chartAttr.legendiconalpha, '100'),
          rawFillColor: color,
          stroke: toRaphaelColor(strokeColor)
        }
      });
      // check if dataset is initiallyhidden
      if (!dataset.getState('visible')) {
        legendItem.setLegendState('hidden');
      } else { // remove hidden state of legend item is dataset is not hidden
        legendItem.removeLegendState('hidden');
      }
    } else if (dataset.config.legendItemId) {
      legend.disposeItem(dataset.config.legendItemId);
    }
  }
  /**
   * Calculate current pos w.r.t prev for an array
   * @param   {Object}  pathVal   current path
   * @param   {Object}  lim    x,y axis limits
   * @return {Array}          Old path
   */
  getOldPath (pathVal, lim) {
    var dataSet = this,
      path = pathVal,
      xLim = lim.x,
      yLim = lim.y,
      oldLim = dataSet.config && dataSet.config.prevLim,
      oldLimX = oldLim.x,
      oldLimY = oldLim.y,
      i = 0,
      ii = path.pathArr.length, // eslint-disable-line good-practices/no-single-usage-variable
      item,
      getOldValX = function (value) {
        // getting ratio by pixels
        let val = value;
        val = (val - oldLimX.minPixel.x) / (oldLimX.maxPixel.x - oldLimX.minPixel.x);
        // ratio to value
        val = val * (oldLimX.max - oldLimX.min) + oldLimX.min;
        // value in old ratio
        val = (val - xLim.min) / (xLim.max - xLim.min);
        // return position in previous
        return val * ((xLim.maxPixel.x - xLim.minPixel.x)) + xLim.minPixel.x + 1;
      },
      getOldValY = function (value) {
        let val = value;
        if ((val < oldLimY.base && val > yLim.base) ||
                        (val > oldLimY.base && val < yLim.base)) {
          val = yLim.base;
        }
        return val - 1;
      },
      getOldValYBase = function (val) {
        if (val === oldLimY.base) {
          return yLim.base;
        }
        return getOldValY(val);
      };
    if (oldLimY.min === yLim.min && oldLimX.min === xLim.min &&
                oldLimY.max === yLim.max && oldLimX.max === xLim.max) {
      return path;
    }
    path = extend2({}, path);
    if (!oldLim) {
      return [];
    }
    // generating new copy of array
    path.pathArr = path.pathArr.slice(0);
    path.path2Arr = path.path2Arr.slice(0);
    for (i = ii; i--;) {
      item = path.pathArr[i].slice(0);
      // check if array
      if (!item[1] || !item.join) {
        continue;
      }
      item[1] = getOldValX(item[1]);
      item[2] = getOldValYBase(item[2]);
      path.pathArr[i] = item;
    }
    for (i = path.path2Arr.length; i--;) {
      item = path.path2Arr[i].slice(0);
      // check if array
      if (!item[1] || !item.join) {
        continue;
      }
      item[1] = getOldValX(item[1]);
      item[2] = getOldValYBase(item[2]);
      path.path2Arr[i] = item;
    }
    return path;
  }
  /**
   * Configure data level
   */
  _setConfigure () {
    var dataSet = this,
      conf = dataSet.config,
      setDataArr = dataSet.config.JSONData.data || [],
      setData,
      dataObj,
      len = dataSet.getFromEnv('xAxis').getTicksLen(), // eslint-disable-line good-practices/no-single-usage-variable
      dataStore,
      i;

    conf.imageCount = 0;
    dataStore = dataSet.components.data;
    if (!dataStore) {
      dataStore = dataSet.components.data = [];
    }
    conf.maxRadius = -Infinity;

    for (i = 0; i < len; i++) {
      dataObj = dataStore[i];
      setData = (setDataArr && setDataArr[i]) || {};

      if (!dataObj) {
        dataObj = dataStore[i] = {};
      }

      if (!dataObj.config) {
        dataStore[i].config = {};
      }

      if (!dataObj.graphics) {
        dataObj.graphics = {};
      }
      dataSet._plotConfigure(i, setData);
    }
  }
}
export default RadarDataset;
